import { useState } from 'react';
import { Loader2, Package, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { StatusPill } from '@/components/ui/status-pill';
import { SelectField } from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import {
  use_create_grooming_package,
  use_delete_grooming_package,
  use_grooming_packages,
  use_update_grooming_package,
} from '@/features/grooming-package/hooks';
import {
  GROOMING_TIER_OPTIONS,
  MAX_INCLUDED_ITEMS,
  type CreateGroomingPackageInput,
  type GroomingPackage,
  type GroomingTier,
} from '@/features/grooming-package/types';

const TIER_TONE: Record<GroomingTier, 'neutral' | 'info' | 'success'> = {
  BASIC: 'neutral',
  PREMIUM: 'info',
  DELUXE: 'success',
};

type FormState = {
  tier: GroomingTier;
  name: string;
  description: string;
  price_inr: string;
  duration_minutes: string;
  mrp_inr: string;
  included_items: string[];
  is_active: boolean;
};

const EMPTY: FormState = {
  tier: 'BASIC',
  name: '',
  description: '',
  price_inr: '',
  duration_minutes: '',
  mrp_inr: '',
  included_items: [],
  is_active: true,
};

function package_to_form(p: GroomingPackage): FormState {
  return {
    tier: p.tier,
    name: p.name,
    description: p.description ?? '',
    price_inr: p.price_inr,
    duration_minutes: p.duration_minutes != null ? String(p.duration_minutes) : '',
    mrp_inr: p.mrp_inr ?? '',
    included_items: [...p.included_items],
    is_active: p.is_active,
  };
}

/** Live discount % preview — mirrors the backend's derived rule exactly. */
function derived_discount_pct(price: number, mrp: number): number | null {
  if (!(mrp > price) || !Number.isFinite(price) || !Number.isFinite(mrp)) return null;
  return Math.round(((mrp - price) / mrp) * 100);
}

function IncludedItemsEditor({
  items,
  on_change,
}: {
  items: string[];
  on_change: (items: string[]) => void;
}) {
  const [draft, set_draft] = useState('');
  const full = items.length >= MAX_INCLUDED_ITEMS;

  function add() {
    const v = draft.trim();
    if (!v || full) return;
    on_change([...items, v]);
    set_draft('');
  }

  function remove(i: number) {
    on_change(items.filter((_, idx) => idx !== i));
  }

  function move(i: number, dir: -1 | 1) {
    const next = i + dir;
    if (next < 0 || next >= items.length) return;
    const copy = [...items];
    [copy[i], copy[next]] = [copy[next], copy[i]];
    on_change(copy);
  }

  return (
    <div className="flex flex-col gap-2" data-testid="included-items-editor">
      <Label className="flex items-center gap-1">
        Included items{' '}
        <span className="text-xs font-normal text-muted-foreground">
          ({items.length}/{MAX_INCLUDED_ITEMS})
        </span>
      </Label>
      {items.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-testid="included-items-list">
          {items.map((item, i) => (
            <li
              key={`${item}-${i}`}
              className="flex items-center gap-1.5 rounded-full border-2 border-input bg-secondary/40 py-1 pl-3 pr-1.5 text-sm"
              data-testid={`included-item-${i}`}
            >
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label="Move earlier"
                className="text-muted-foreground disabled:opacity-30"
              >
                ‹
              </button>
              <span>{item}</span>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={i === items.length - 1}
                aria-label="Move later"
                className="text-muted-foreground disabled:opacity-30"
              >
                ›
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label={`Remove ${item}`}
                data-testid={`remove-included-item-${i}`}
                className="rounded-full p-0.5 hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => set_draft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Bath & blow-dry"
          disabled={full}
          data-testid="included-item-input"
        />
        <Button type="button" variant="outline" onClick={add} disabled={full || !draft.trim()} data-testid="included-item-add">
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>
    </div>
  );
}

export function GroomingPackagesPage() {
  const packages_q = use_grooming_packages();
  const create_m = use_create_grooming_package();
  const update_m = use_update_grooming_package();
  const delete_m = use_delete_grooming_package();

  const packages = packages_q.data ?? [];
  const [editing, set_editing] = useState<null | { id?: string }>(null);
  const [form, set_form] = useState<FormState>(EMPTY);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});

  function open_new() {
    set_error(null);
    set_field_errors({});
    set_form(EMPTY);
    set_editing({});
  }
  function open_edit(p: GroomingPackage) {
    set_error(null);
    set_field_errors({});
    set_form(package_to_form(p));
    set_editing({ id: p.id });
  }
  function close_form() {
    set_editing(null);
    set_error(null);
    set_field_errors({});
  }

  const price = Number(form.price_inr);
  const mrp = form.mrp_inr.trim() === '' ? null : Number(form.mrp_inr);
  const preview_discount = mrp != null ? derived_discount_pct(price, mrp) : null;

  const saving = create_m.isPending || update_m.isPending;

  function validate(): CreateGroomingPackageInput | string {
    if (!form.name.trim() || form.name.trim().length < 2) return 'Give this package a name (at least 2 characters).';
    if (!Number.isFinite(price) || price <= 0) return 'Enter a price greater than 0.';
    const duration = Number(form.duration_minutes);
    if (!Number.isInteger(duration) || duration <= 0) return 'Duration must be a whole number of minutes.';
    if (mrp != null) {
      if (!Number.isFinite(mrp) || mrp <= 0) return 'Enter a valid MRP, or leave it blank.';
      if (!(mrp > price)) return 'MRP must be greater than the price.';
    }
    if (form.included_items.length < 1) return 'Add at least one included item.';
    if (form.included_items.length > MAX_INCLUDED_ITEMS) return `A package can list at most ${MAX_INCLUDED_ITEMS} included items.`;

    return {
      tier: form.tier,
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      price_inr: price,
      duration_minutes: duration,
      mrp_inr: mrp ?? undefined,
      included_items: form.included_items,
      is_active: form.is_active,
    };
  }

  async function submit() {
    set_error(null);
    set_field_errors({});
    const result = validate();
    if (typeof result === 'string') return set_error(result);
    try {
      if (editing?.id) {
        await update_m.mutateAsync({ id: editing.id, input: result });
      } else {
        await create_m.mutateAsync(result);
      }
      close_form();
    } catch (err) {
      if (err instanceof ApiError && err.error_code === 'INVALID_MRP') {
        set_field_errors({ mrp_inr: err.message });
        set_error('Please fix the highlighted field below.');
        return;
      }
      set_field_errors(field_errors_from(err));
      set_error(form_error_message(err, 'Could not save this package.', { highlights_fields: true }));
    }
  }

  async function toggle_active(p: GroomingPackage) {
    try {
      await update_m.mutateAsync({ id: p.id, input: { is_active: !p.is_active } });
    } catch {
      /* surfaced via list refetch; ignore inline */
    }
  }

  async function remove(p: GroomingPackage) {
    if (!window.confirm(`Delete the “${p.name}” package? This cannot be undone.`)) return;
    await delete_m.mutateAsync(p.id);
  }

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Packages"
        description="Curated tiers — Basic, Premium, Deluxe — priced as a single appointment."
        action={
          !editing && (
            <Button onClick={open_new} variant="gradient" size="sm" data-testid="add-package">
              <Plus className="h-4 w-4" /> Add package
            </Button>
          )
        }
      />

      {editing && (
        <Card data-testid="package-form">
          <div className="flex items-center justify-between border-b-2 border-brand-outline-variant/15 px-5 py-3.5">
            <h2 className="font-heading text-base font-bold">{editing.id ? 'Edit package' : 'New package'}</h2>
            <Button variant="ghost" size="icon" onClick={close_form} aria-label="Close" data-testid="package-cancel">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pkg-tier">Tier</Label>
                <SelectField
                  id="pkg-tier"
                  data-testid="pkg-tier"
                  value={form.tier}
                  onValueChange={(v) => set_form((f) => ({ ...f, tier: v as GroomingTier }))}
                  options={GROOMING_TIER_OPTIONS}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pkg-name">Name</Label>
                <Input
                  id="pkg-name"
                  data-testid="pkg-name"
                  value={form.name}
                  onChange={(e) => set_form((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Premium Grooming Package"
                />
                {field_errors.name && <p className="text-xs text-destructive">{field_errors.name}</p>}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pkg-price">Price (₹)</Label>
                <Input
                  id="pkg-price"
                  data-testid="pkg-price"
                  type="number"
                  min={1}
                  value={form.price_inr}
                  onChange={(e) => set_form((f) => ({ ...f, price_inr: e.target.value }))}
                  placeholder="899"
                />
                {field_errors.price_inr && <p className="text-xs text-destructive">{field_errors.price_inr}</p>}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pkg-duration" className="flex items-center gap-1">
                  Duration <span className="text-xs font-normal text-muted-foreground">(min)</span>
                </Label>
                <Input
                  id="pkg-duration"
                  data-testid="pkg-duration"
                  type="number"
                  min={1}
                  value={form.duration_minutes}
                  onChange={(e) => set_form((f) => ({ ...f, duration_minutes: e.target.value }))}
                  placeholder="90"
                />
                {field_errors.duration_minutes && (
                  <p className="text-xs text-destructive">{field_errors.duration_minutes}</p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pkg-mrp" className="flex items-center gap-1">
                  MRP <span className="text-xs font-normal text-muted-foreground">(₹, optional — struck-through)</span>
                </Label>
                <Input
                  id="pkg-mrp"
                  data-testid="pkg-mrp"
                  type="number"
                  min={1}
                  value={form.mrp_inr}
                  onChange={(e) => set_form((f) => ({ ...f, mrp_inr: e.target.value }))}
                  placeholder="1199"
                  className={field_errors.mrp_inr ? 'border-destructive' : undefined}
                />
                {field_errors.mrp_inr ? (
                  <p className="text-xs text-destructive" data-testid="pkg-mrp-error">
                    {field_errors.mrp_inr}
                  </p>
                ) : preview_discount != null ? (
                  <p className="text-xs font-semibold text-success" data-testid="pkg-discount-preview">
                    {preview_discount}% OFF — matches what customers will see
                  </p>
                ) : mrp != null && Number.isFinite(mrp) && Number.isFinite(price) && mrp <= price ? (
                  <p className="text-xs text-destructive">MRP must be greater than the price.</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label htmlFor="pkg-desc" className="flex items-center gap-1">
                  Description <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                </Label>
                <textarea
                  id="pkg-desc"
                  data-testid="pkg-desc"
                  value={form.description}
                  onChange={(e) => set_form((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="rounded-input border-2 border-input bg-card px-4 py-3 text-base text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15"
                  placeholder="A relaxing full-service groom…"
                />
              </div>

              <div className="sm:col-span-2">
                <IncludedItemsEditor
                  items={form.included_items}
                  on_change={(items) => set_form((f) => ({ ...f, included_items: items }))}
                />
                {field_errors.included_items && (
                  <p className="mt-1 text-xs text-destructive">{field_errors.included_items}</p>
                )}
              </div>

              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-input border-2 border-input bg-secondary/40 px-4 py-2.5 sm:col-span-2">
                <span className="flex flex-col">
                  <span className="text-sm font-semibold">Active</span>
                  <span className="text-xs text-muted-foreground">Visible to customers and bookable</span>
                </span>
                <input
                  type="checkbox"
                  data-testid="pkg-active"
                  checked={form.is_active}
                  onChange={(e) => set_form((f) => ({ ...f, is_active: e.target.checked }))}
                  className="h-5 w-5 shrink-0 accent-primary"
                />
              </label>
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="package-form-error" role="alert">
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <Button onClick={submit} variant="gradient" disabled={saving} data-testid="pkg-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editing.id ? 'Save changes' : 'Create package'}
              </Button>
              <Button onClick={close_form} variant="ghost">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {packages_q.isLoading ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : packages_q.error ? (
        <ErrorState
          data-testid="packages-error"
          title="Failed to load packages"
          description={form_error_message(packages_q.error, 'Please try again.')}
        />
      ) : packages.length === 0 ? (
        <EmptyState
          data-testid="packages-empty"
          icon={Package}
          title="No packages yet"
          description="Bundle your services into Basic, Premium, and Deluxe tiers."
          action={
            <Button onClick={open_new} variant="gradient">
              <Plus className="h-4 w-4" /> Add package
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="packages-list">
          {packages.map((p) => (
            <Card key={p.id} data-testid={`package-${p.id}`}>
              <CardContent className="flex flex-col gap-3 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <StatusPill tone={TIER_TONE[p.tier]}>{p.tier}</StatusPill>
                    <h3 className="mt-1 font-heading text-base font-bold">{p.name}</h3>
                  </div>
                  {!p.is_active && <StatusPill tone="warning">Inactive</StatusPill>}
                </div>

                {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}

                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-lg font-bold tabular-nums">₹{p.price_inr}</span>
                  {p.mrp_inr && (
                    <span className="text-xs text-muted-foreground line-through tabular-nums">₹{p.mrp_inr}</span>
                  )}
                  {p.discount_pct != null && (
                    <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs font-semibold text-success" data-testid={`package-discount-${p.id}`}>
                      {p.discount_pct}% OFF
                    </span>
                  )}
                </div>
                {p.duration_minutes != null && (
                  <p className="text-xs text-muted-foreground">{p.duration_minutes} min</p>
                )}

                {p.included_items.length > 0 && (
                  <ul className="flex flex-wrap gap-1.5">
                    {p.included_items.map((item, i) => (
                      <li key={i} className="rounded-full bg-secondary/60 px-2 py-0.5 text-xs">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-1 flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => toggle_active(p)} data-testid={`pkg-toggle-${p.id}`}>
                    {p.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => open_edit(p)} data-testid={`pkg-edit-${p.id}`} aria-label="Edit">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`pkg-delete-${p.id}`} aria-label="Delete">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
